import Nango from '@nangohq/frontend';
import type { ConnectUIEvent } from '@nangohq/frontend';

import * as flashActions from 'pkg/actions/flashes';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { create } from 'pkg/api/models/actions';
import * as sdk from 'pkg/core/sdk';
import { crash } from 'pkg/errors/errors';

// onClose runs when the Connect UI closes, connected or not. The connection
// itself is saved by the Nango webhook, so refetch rather than trust the UI.
export async function connect(
	organizationId: number,
	provider: models.integrationConnection.Provider,
	onClose: () => void
): Promise<void> {
	const [request, session, error] = await create<
		null,
		models.integrationConnection.ConnectSession
	>(
		endpoints.Integrations.CreateConnectSession(organizationId, provider),
		null
	);

	if (!request.ok) {
		flashActions.show({
			title: crash().title,
			message: error.error,
		});

		return;
	}

	const handleEvent = (event: ConnectUIEvent) => {
		if (event.type === 'close') {
			onClose();
		}
	};

	new Nango().openConnectUI({
		sessionToken: session.token,
		onEvent: handleEvent,
	});
}

export type ConnectLOKResult = 'connected' | 'organization_not_found' | 'failed';

// An organization number RF doesn't know is left to the caller to show next to the field.
export async function connectLOK(
	organizationId: number,
	payload: models.integrationConnection.ConnectLOKPayload
): Promise<ConnectLOKResult> {
	const request = await sdk.put(
		endpoints.Integrations.ConnectLOK(organizationId),
		{},
		payload
	);

	if (request.status === 400) {
		return 'organization_not_found';
	}

	if (!request.ok) {
		flashActions.show({ title: crash().title }, request.status);

		return 'failed';
	}

	return 'connected';
}

export async function pickXeroTenant(
	organizationId: number,
	tenantId: string
): Promise<boolean> {
	const request = await sdk.put(
		endpoints.Integrations.UpdateXeroTenant(organizationId),
		{},
		{ tenantId }
	);

	if (!request.ok) {
		const error: models.APIError = await request.json();

		flashActions.show({ title: crash().title, message: error.error });

		return false;
	}

	return true;
}

export async function saveXeroSettings(
	organizationId: number,
	payload: models.integrationConnection.XeroSettingsPayload
): Promise<models.integrationConnection.XeroSettingsDetails> {
	const request = await sdk.put(
		endpoints.Integrations.UpdateXeroSettings(organizationId),
		{},
		payload
	);

	if (!request.ok) {
		const error: models.APIError = await request.json();

		flashActions.show({ title: crash().title, message: error.error });

		return null;
	}

	return request.json();
}

export async function searchXeroContacts(
	organizationId: number,
	search: string
): Promise<models.integrationConnection.XeroContact[]> {
	const request = await sdk.get(
		endpoints.Integrations.XeroContacts(organizationId),
		{ search }
	);

	if (!request.ok) {
		const error: models.APIError = await request.json();

		flashActions.show({ title: crash().title, message: error.error });

		return [];
	}

	return request.json();
}

export async function createXeroFeeContact(
	organizationId: number
): Promise<models.integrationConnection.XeroContact> {
	const request = await sdk.post(
		endpoints.Integrations.CreateXeroFeeContact(organizationId)
	);

	if (!request.ok) {
		const error: models.APIError = await request.json();

		flashActions.show({ title: crash().title, message: error.error });

		return null;
	}

	return request.json();
}

export async function disconnect(
	organizationId: number,
	provider: models.integrationConnection.Provider
): Promise<boolean> {
	const request = await sdk.destroy(
		endpoints.Integrations.Disconnect(organizationId, provider)
	);

	if (!request.ok) {
		flashActions.show({ title: crash().title }, request.status);

		return false;
	}

	return true;
}
